import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Card, Progress, Tabs } from "antd";
import StandardButton from '../components/StandardButton';
import { toast } from "react-hot-toast";
import Header from "../components/Header";
import Footer from "../components/Footer";
import api from "../interceptors/api";
import { useTranslation } from "react-i18next";

export default function AudioQuizTake() {
  const { id } = useParams();
  const [quiz, setQuiz] = useState(null);
  const [started, setStarted] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [audioUrl, setAudioUrl] = useState("");
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [leadSubmitting, setLeadSubmitting] = useState(false);
  const [showSummary, setShowSummary] = useState(false);

  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState(false);

  const [searchParams] = useSearchParams();
  const isEmbedded = searchParams.get("embed") === "true";

  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const { t } = useTranslation();
  
  useEffect(() => {
    const storedName = localStorage.getItem("audioQuizName");
    const storedEmail = localStorage.getItem("audioQuizEmail");
    if (storedName) setName(storedName);
    if (storedEmail) setEmail(storedEmail);
  }, []);

  // Certificate (final) state via /audio-quiz/certificate/create
  const [activeTab, setActiveTab] = useState("2"); // default to Results
  const [certLoading, setCertLoading] = useState(false);
  const [publicCertUrl, setPublicCertUrl] = useState(""); // S3 public URL returned by create
  const [certId, setCertId] = useState("");               // token/ID from backend
  const [linkedinAddUrl, setLinkedinAddUrl] = useState("");

  const { TabPane } = Tabs;

  // Fetch Quiz
  useEffect(() => {
    const fetchQuiz = async () => {
      try {
        const res = await api.get(`/audio-quiz/share/${id}`);
        setQuiz(res.data.quiz);
        setAnswers(Array(res.data.quiz.questions.length).fill(null));
      } catch (err) {
        toast.error(t("load_quiz_failed"));
      }
    };
    if (id) fetchQuiz();
  }, [id]);

  // Fetch Stats
  useEffect(() => {
    const fetchStats = async () => {
      setStatsLoading(true);
      setStatsError(false);
      try {
        const res = await api.get(`/audio-quiz/analytics/${id}`);
        setStats(res.data); // { total_started, total_completed, unique_users }
      } catch (err) {
        setStatsError(true);
        setStats(null);
      } finally {
        setStatsLoading(false);
      }
    };
    if (id) fetchStats();
  }, [id]);

  // Fetch Audio for each question
  useEffect(() => {
    if (quiz && started && currentQuestion < quiz.questions.length) {
      const fetchAudio = async () => {
        const text = quiz.questions[currentQuestion].question;
        const params = new URLSearchParams({
          text,
          languageCode: "en-US",
          languageName: "en-US-Neural2-J",
          audioFormat: "mp3",
        });
        try {
          const response = await api.get(`/text-to-speech?${params.toString()}`, {
            responseType: "blob",
          });
          const blob = response.data;
          const url = URL.createObjectURL(blob);
          setAudioUrl(url);
        } catch (err) {
          toast.error(t("audio_load_failed"));
        }
      };
      fetchAudio();
    }
  }, [currentQuestion, quiz, started]);

  const handleAnswer = (option) => {
    const newAnswers = [...answers];
    newAnswers[currentQuestion] = option;
    setAnswers(newAnswers);
  };

  const calculateScore = () =>
    answers.filter((ans, idx) => ans === quiz.questions[idx].answer).length;

  const handleNext = async () => {
    if (currentQuestion < quiz.questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      try {
        const scorePct = parseInt((calculateScore() * 100) / quiz.questions.length);
        await api.post("/audio-quiz/track/complete", { quizId: id, score: scorePct });
      } catch (_) {
      }
      if (localStorage.getItem("audioQuizName") && localStorage.getItem("audioQuizEmail")) {
        // Auto-submit lead if stored
        setName(localStorage.getItem("audioQuizName"));
        setEmail(localStorage.getItem("audioQuizEmail"));
        await handleLeadSubmit();
      } else {
        setShowLeadForm(true);
      }
    }
  };

  const validEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.trim());
  const validName = (n) => n.trim().length > 1;

  const getSourceDomain = () => {
    try {
      const url = new URL(document.referrer);
      return url.hostname;
    } catch {
      return "";
    }
  };

  const handleLeadSubmit = async () => {
    if (!validName(name) || !validEmail(email)) {
      toast.error(t("enter_valid_name_email"));
      return;
    }
    setLeadSubmitting(true);
    try {
      const score = calculateScore();
      const percentage = Math.round((score * 100) / quiz.questions.length);
      let sourceDomain = "";
      try {
        const url = new URL(document.referrer);
        sourceDomain = url.hostname;
      } catch {
        sourceDomain = "";
      }

      await api.post("/audio-quiz/track/lead", {
        quizId: id,
        quizTitle: quiz?.title,
        name: name.trim(),
        email: email.trim(),
        total: quiz?.questions?.length,
        score,
        percentage,
        sourceDomain,
      });

      localStorage.setItem("audioQuizName", name.trim());
      localStorage.setItem("audioQuizEmail", email.trim());

      setShowLeadForm(false);
      setShowSummary(true);
    } catch (err) {
      setShowLeadForm(false);
      setShowSummary(true);
    } finally {
      setLeadSubmitting(false);
    }
  };

  // ===== Certificate Create (final) =====
  const createCertificate = async () => {
    if (!quiz) return;
    if (publicCertUrl) return; // already created this session
    setCertLoading(true);
    try {
      const payload = {
        quizId: id,
        quizTitle: quiz.title,
        total: quiz.questions.length,
        score: calculateScore(),
        sourceDomain: getSourceDomain(),
      };

      const resp = await api.post("/audio-quiz/certificate/create", payload);
      if (!resp.data?.success) {
        throw new Error(resp.data?.error || "Certificate creation failed");
      }

      setPublicCertUrl(resp.data.url || "");
      setCertId(resp.data.certId || "");
      setLinkedinAddUrl(resp.data.linkedin?.addToProfileUrl || "");
    } catch (e) {
      toast.error(t("certificate_creation_failed"));
    } finally {
      setCertLoading(false);
    }
  };

  // Auto-create certificate when Certificate tab opens
  useEffect(() => {
    if (activeTab === "3" && quiz && !publicCertUrl && !certLoading) {
      createCertificate();
    }
  }, [activeTab, quiz]);

  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  if (!quiz) {
    return (
      <div className="min-h-screen">
        <main className="text-center mt-10 text-lg text-gray-700">{t("loading_quiz")}</main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {!isEmbedded && <Header />}
      <main className="flex items-center justify-center min-h-[calc(100vh-100px)] px-4">
        <div className="w-full max-w-5xl">
          {!started ? (
            <Card className="max-w-5xl mx-auto p-6 text-center">
              <h1 className="text-5xl font-bold mb-4">{quiz.title || t("audio_quiz")}</h1>
              <p className="text-gray-600 mb-6">
                {t("quiz_description")}
              </p>
              <StandardButton
                type="secondary"
                size="large"
                className="mb-6"
                onClick={async () => {
                  setStarted(true);
                  try {
                    await api.post("/audio-quiz/track/start", { quizId: id });
                  } catch (_) {
                  }
                }}
              >
                {t("start_quiz")}
              </StandardButton>

              {/* Analytics */}
              {statsLoading ? (
                <div className="mb-4 text-gray-400">{t("loading_stats")}</div>
              ) : statsError ? (
                <div className="mb-4 text-red-500">{t("load_stats_failed")}</div>
              ) : stats &&
                (stats.total_started > 0 ||
                  stats.total_completed > 0 ||
                  stats.unique_users > 0) ? (
                <>
                  <div className="mb-2 text-sm text-gray-500">
                    <strong>{t("analytics")}:</strong> {t("analytics_description")}
                  </div>
                  <div className="flex justify-center gap-6 mb-6">
                    <div className="flex-1">
                      <div className="rounded-2xl p-4 bg-gradient-to-tr from-indigo-50 to-blue-100 text-blue-900 flex flex-col items-center border border-blue-100">
                        <div className="text-2xl font-extrabold">{stats.total_started ?? 0}</div>
                        <div className="mt-1 text-xs tracking-wide uppercase opacity-80">{t("started")}</div>
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="rounded-2xl p-4 bg-gradient-to-tr from-green-50 to-emerald-100 text-emerald-900 flex flex-col items-center border border-emerald-100">
                        <div className="text-2xl font-extrabold">{stats.total_completed ?? 0}</div>
                        <div className="mt-1 text-xs tracking-wide uppercase opacity-80">{t("completed")}</div>
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="rounded-2xl p-4 bg-gradient-to-tr from-pink-50 to-rose-100 text-rose-900 flex flex-col items-center border border-rose-100">
                        <div className="text-2xl font-extrabold">{stats.unique_users ?? 0}</div>
                        <div className="mt-1 text-xs tracking-wide uppercase opacity-80">{t("total_users")}</div>
                      </div>
                    </div>
                  </div>
                </>
              ) : null}
            </Card>
          ) : showLeadForm ? (
            <Card className="max-w-2xl mx-auto p-6">
              <h2 className="text-2xl font-bold mb-2">{t("get_your_results")}</h2>
              <p className="text-gray-700 mb-6">
                <span className="text-gray-500">{t("results_disclaimer")}</span>
              </p>

              <label className="block text-left mb-1 font-medium">{t("full_name")}</label>
              <input
                type="text"
                placeholder={t("enter_full_name")}
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full border px-4 py-2 rounded mb-4"
              />

              <label className="block text-left mb-1 font-medium">{t("email_address")}</label>
              <input
                type="email"
                placeholder={t("enter_email_address")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border px-4 py-2 rounded mb-6"
              />

              <div className="flex gap-3">
                <StandardButton
                  type="secondary"
                  onClick={handleLeadSubmit}
                  disabled={leadSubmitting || !validName(name) || !validEmail(email)}
                >
                  {leadSubmitting ? t("submitting") : t("see_my_results")}
                </StandardButton>
              </div>
            </Card>
          ) : showSummary ? (
            <Card className="max-w-5xl mx-auto p-6">
              <Tabs defaultActiveKey="2" activeKey={activeTab} onChange={setActiveTab}>
                <TabPane tab={t("results")} key="2">
                  <h2 className="text-2xl font-bold mb-2">{t("your_score")}</h2>
                  <p className="text-lg mb-4">
                    {t("score_message", { 
                      correct: calculateScore(), 
                      total: quiz.questions.length, 
                      percentage: Math.round((calculateScore() * 100) / quiz.questions.length) 
                    })}
                  </p>
                  <ul className="space-y-3">
                    {quiz.questions.map((q, idx) => {
                      const isCorrect = answers[idx] === q.answer;
                      return (
                        <li
                          key={idx}
                          className={`border p-3 rounded ${isCorrect ? "bg-green-100 border-green-500" : "bg-red-100 border-red-500"
                            }`}
                        >
                          <p>
                            <strong>{t("question")}:</strong> {q.question}
                          </p>
                          <p>
                            <strong>{t("your_answer")}:</strong>{" "}
                            <span className={isCorrect ? "text-green-800" : "text-red-800"}>
                              {answers[idx] || t("no_answer")}
                            </span>
                          </p>

                          {!isCorrect && (
                            <>
                              <p>
                                <strong>{t("correct_answer")}:</strong>{" "}
                                <span className="text-green-800">{q.answer}</span>
                              </p>

                              {/* 🔽 Explanation for failed answers */}
                              {q.explanation ? (
                                <div className="mt-3 p-3 rounded-lg bg-yellow-50 border border-yellow-200">
                                  <p className="font-semibold">{t("explanation")}</p>
                                  <p className="mt-1 text-sm whitespace-pre-line">
                                    {q.explanation}
                                  </p>
                                </div>
                              ) : null}
                            </>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                  <div className="text-left mt-4">
                      <StandardButton
                        type="secondary"
                        onClick={() => window.location.reload()}>
                        {t("restart_quiz")}
                      </StandardButton>
                  </div>
                </TabPane>

                <TabPane tab={t("certificate")} key="3">
                  <div className="flex flex-col gap-4 mb-4 md:flex-row md:items-center md:justify-between">
                    <div>
                      <div className="text-sm">
                        {t("add_certificate_to_linkedin")}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {!publicCertUrl && (
                        <StandardButton
                          type="secondary"
                          onClick={createCertificate}
                          disabled={certLoading}
                        >
                          {certLoading ? t("creating") : t("create_certificate")}
                        </StandardButton>
                      )}

                      {linkedinAddUrl ? (
                        <a
                          href={linkedinAddUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <img
                            src="/linkedIn_add_profile.png"
                            alt={t("add_to_linkedin")}
                            style={{ height: 32 }}
                          />
                        </a>
                      ) : null}
                    </div>
                  </div>

                  <div className="w-full border rounded-xl overflow-hidden">
                    {publicCertUrl ? (
                      <iframe
                        title="Certificate"
                        src={publicCertUrl}
                        className="w-full"
                        style={{ height: 900, border: "none" }}
                      />
                    ) : (
                      <div className="p-6 text-gray-500">
                        {certLoading ? t("generating_certificate") : t("no_certificate_yet")}
                      </div>
                    )}
                  </div>
                </TabPane>
              </Tabs>
            </Card>
          ) : (
            <Card className="max-w-5xl mx-auto p-6">
              <h2 className="text-xl font-bold mb-2">{quiz.questions[currentQuestion].title}</h2>
              <Progress percent={Math.round(((currentQuestion + 1) / quiz.questions.length) * 100)} />
              <p className="text-lg font-semibold my-4">{quiz.questions[currentQuestion].question}</p>
              {audioUrl ? (
                <audio controls autoPlay src={audioUrl} className="w-full my-3" />
              ) : (
                <p>{t("loading_audio")}</p>
              )}
              <div className="space-y-3 mt-4">
                {quiz.questions[currentQuestion].options.map((opt, idx) => {
                  const isSelected = answers[currentQuestion] === opt;
                  return (
                    <StandardButton
                      key={idx}
                      type={isSelected ? "primary" : "secondary"}
                      onClick={() => handleAnswer(opt)}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left justify-start"
                    >
                      <span
                        className={`w-5 h-5 flex items-center justify-center border rounded-full 
                        ${isSelected ? "bg-white border-white" : "border-gray-400"}`}
                      >
                        {isSelected && <span className="w-3 h-3 rounded-full bg-blue-600"></span>}
                      </span>
                      <span className="flex-1 font-medium">{opt}</span>
                    </StandardButton>
                  );
                })}
              </div>

              <div className="flex justify-between mt-6">
                <StandardButton 
                  type="secondary" 
                  disabled={currentQuestion === 0} 
                  onClick={handleBack}
                >
                  {t("back")}
                </StandardButton>
                <StandardButton 
                  type="secondary" 
                  onClick={handleNext} 
                  disabled={!answers[currentQuestion]}
                >
                  {currentQuestion === quiz.questions.length - 1 ? t("finish") : t("next")}
                </StandardButton>
              </div>
            </Card>
          )}
        </div>
      </main>
      {!isEmbedded && <Footer />}
    </div>
  );
}
